import React, { useMemo, useState, useEffect } from 'react';
import { useForm, FormProvider, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import './tba-wizard.css';

// const BASE_URL = 'http://localhost:8080';


const BASE_URL = 'https://pidslinkapi.ren.africa';



const steps = [
  { id: 1, key: 'submitter' },
  { id: 2, key: 'team' },
  { id: 3, key: 'teamType' },
  { id: 4, key: 'constituency' },
  { id: 5, key: 'contact' },
  { id: 6, key: 'reps' },
  { id: 7, key: 'memberships' },
  { id: 8, key: 'cooperationAndCollaboration' },
  { id: 9, key: 'review' },
];

const defaultValues = {
  submitter: { first_name: '', last_name: '', email: '', pgpFingerprint: '', phoneNumber: '', role: '' },
  team: { acronym: '', teamName: '', hostOrg: '', mainCountry: '', additionalCountries: '', establishmentDate: '' },
  teamType: { types: [], otherType: '' },
  constituency: '',
  contact: { postalAddress: '', email: '', primaryPhone: '', emergencyPhone: '', primaryPgp: '', webServer: '', businessHours: '', timeZone: '', profile: undefined },
  reps: { primary: { name: '', email: '', pgp: '', phone: '' }, secondary: { name: '', email: '', pgp: '', phone: '' } },
  memberships: { firstMemberSince: '', tfCsirtListedSince: '', tfCsirtAccreditedSince: '', forumName: '', forumUrl: '' },
  cooperationAndCollaboration: '',
};

const stepRequiredFields = {
  submitter: ['submitter.first_name', 'submitter.last_name', 'submitter.email'],
  team: ['team.teamName', 'team.mainCountry'],
  teamType: [],
  constituency: [],
  contact: [],
  reps: ['reps.primary.name'],
  memberships: [],
  cooperationAndCollaboration: [],
};

function StepHeader({ current }) {
  const pct = Math.round(((current + 1) / steps.length) * 100);
  return (
    <div className="tba-stepper">
      <div className="tba-stepper__meta">
        <span>Step {current + 1} / {steps.length}</span>
        <span>{pct}%</span>
      </div>
      <div className="tba-progress"><div className="tba-progress__bar" style={{ width: pct + '%' }} /></div>
    </div>
  );
}

function FieldError({ name }) {
  const { formState: { errors } } = useFormContext();
  const path = name.split('.');
  let node = errors;
  for (let i = 0; i < path.length; i++) node = node?.[path[i]];
  const msg = node?.message;
  return msg ? <p className="tba-error">{String(msg)}</p> : null;
}

function Input({ name, label, type = 'text', placeholder = '', rules }) {
  const { register } = useFormContext();
  return (
    <div className="tba-field">
      <label className="tba-label">{label}</label>
      <input {...register(name, rules)} type={type} placeholder={placeholder} className="tba-input" />
      <FieldError name={name} />
    </div>
  );
}

function TextArea({ name, label, rows = 4, rules }) {
  const { register } = useFormContext();
  return (
    <div className="tba-field">
      <label className="tba-label">{label}</label>
      <textarea {...register(name, rules)} rows={rows} className="tba-textarea" />
      <FieldError name={name} />
    </div>
  );
}

function FileInput({ name, label, accept = 'image/*' }) {
  const { register } = useFormContext();
  return (
    <div className="tba-field">
      <label className="tba-label">{label}</label>
      <input {...register(name)} type="file" accept={accept} className="tba-file" />
      <FieldError name={name} />
    </div>
  );
}

function CheckboxGroup({ name, label, options }) {
  const { watch, setValue } = useFormContext();
  const selected = watch(name) || [];
  const toggle = (val) => {
    const exists = selected.includes(val);
    const next = exists ? selected.filter((v) => v !== val) : [...selected, val];
    setValue(name, next, { shouldDirty: true });
  };
  return (
    <div className="tba-field">
      <label className="tba-label">{label}</label>
      <div className="tba-checkbox-grid">
        {options.map((o) => (
          <label key={o.value} className={`tba-checkbox ${selected.includes(o.value) ? 'tba-checkbox--active' : ''}`}>
            <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} className="tba-checkbox__input" />
            <span className="tba-checkbox__box" />
            <span>{o.text}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function SubmitterStep() {
  const { t } = useTranslation();
  return (
    <div>
      <Input name="submitter.first_name" label={t('First name')} rules={{ required: 'Required' }} />
      <Input name="submitter.last_name" label={t('Last name')} rules={{ required: 'Required' }} />
      <Input name="submitter.email" label={t('Email address')} type="email" rules={{ required: 'Required' }} />
      <Input name="submitter.pgpFingerprint" label={t('PGP fingerprint')} />
      <Input name="submitter.phoneNumber" label={t('Phone number including country code')} />
      <TextArea name="submitter.role" label={t('Describe your role in which capacity you submitted this request')} />
    </div>
  );
}

function TeamStep() {
  const { t } = useTranslation();
  return (
    <div>
      <Input name="team.acronym" label={t('Acronym used')} />
      <Input name="team.teamName" label={t('Official team name')} rules={{ required: 'Required' }} />
      <Input name="team.hostOrg" label={t('Host organisation')} />
      <Input name="team.mainCountry" label={t('Main Country')} rules={{ required: 'Required' }} />
      <Input name="team.additionalCountries" label={t('Additional countries')} />
      <Input name="team.establishmentDate" label={t('Team Establishment Date')} type="date" />
    </div>
  );
}

function TeamTypeStep() {
  const { t } = useTranslation();
  const options = useMemo(() => [
    'CIIP', 'Commercial Organisation', 'Financial Sector', 'Government', 'International Body', 'ISP Customer Base', 'Law Enforcement', 'Military', 'National', 'Non-Commercial Organisation', 'Research & Education Network', 'Service Provider Customer Base', 'Vendor Customer Base',
  ].map(v => ({ value: v, text: t(v) })), [t]);
  return (
    <div>
      <CheckboxGroup name="teamType.types" label={t('Type')} options={options} />
      <Input name="teamType.otherType" label={t('Other type of team')} />
    </div>
  );
}

function ConstituencyStep() {
  const { t } = useTranslation();
  return <TextArea name="constituency" label={`(${t('Internet Domains')})`} rows={5} />;
}

function ContactStep() {
  const { t } = useTranslation();
  return (
    <div>
      <TextArea name="contact.postalAddress" label={t('Postal Mailing Address of the team including country')} rows={3} />
      <Input name="contact.email" label={t('Team Email address')} type="email" />
      <Input name="contact.primaryPhone" label={t('Phone number including country code')} />
      <Input name="contact.emergencyPhone" label={t('Emergency phone number including country code')} />
      <Input name="contact.primaryPgp" label={t('PGP fingerprint')} />
      <Input name="contact.webServer" label={t('World Wide Web server')} />
      <Input name="contact.businessHours" label={t('Business hours')} />
      <Input name="contact.timeZone" label={t('Time-zone')} />
      <FileInput name="contact.profile" label={t('Team profile/logo (optional)')} />
    </div>
  );
}

function RepsStep() {
  const { t } = useTranslation();
  return (
    <div className="tba-two-col">
      <div>
        <h4 className="tba-subtitle">* {t('Primary Team Representative')}</h4>
        <Input name="reps.primary.name" label={t('First and last name')} rules={{ required: 'Required' }} />
        <Input name="reps.primary.email" label={t('Email address')} type="email" />
        <Input name="reps.primary.pgp" label={t('PGP fingerprint')} />
        <Input name="reps.primary.phone" label={t('Phone number including country code')} />
      </div>
      <div>
        <h4 className="tba-subtitle">* {t('Secondary Team Representative')}</h4>
        <Input name="reps.secondary.name" label={t('First and last name')} />
        <Input name="reps.secondary.email" label={t('Email address')} type="email" />
        <Input name="reps.secondary.pgp" label={t('PGP fingerprint')} />
        <Input name="reps.secondary.phone" label={t('Phone number including country code')} />
      </div>
    </div>
  );
}

function MembershipsStep() {
  const { t } = useTranslation();
  return (
    <div>
      <p className="tba-help">{t('team is a member of FIRST')}:</p>
      <Input name="memberships.firstMemberSince" label={t('Full member since')} />
      <p className="tba-help">{t('If the team is a member')}:</p>
      <Input name="memberships.tfCsirtListedSince" label={t('Listed since')} />
      <Input name="memberships.tfCsirtAccreditedSince" label={t('Accredited since')} />
      <p className="tba-help">{t('related forum or organisation')}:</p>
      <Input name="memberships.forumName" label={t('Name')} />
      <Input name="memberships.forumUrl" label={t('URL public information')} />
    </div>
  );
}

function CoopStep() {
  const { t } = useTranslation();
  return <TextArea name="cooperationAndCollaboration" label={`${t('Cooperation and Collaboration')} — ${t('basic information ')}`} rows={5} />;
}

function ReviewStep({ allValues }) {
  const { t } = useTranslation();

  const get = (obj, path) => path.split('.').reduce((o, k) => (o ? o[k] : undefined), obj);
  const val = (v) => (v && String(v).trim() ? v : '—');
  const Chips = ({ items }) => (
    <div className="tba-chips">
      {Array.isArray(items) && items.length
        ? items.map((x, i) => (
            <span key={i} className="tba-chip">{x}</span>
          ))
        : <span className="tba-empty">—</span>}
    </div>
  );

  const KV = ({ label, value }) => (
    <div className="tba-kv__row">
      <div className="tba-kv__key">{label}</div>
      <div className="tba-kv__val">{val(value)}</div>
    </div>
  );

  const Section = ({ title, children }) => (
    <section className="tba-card">
      <h4 className="tba-card__title">{title}</h4>
      {children}
    </section>
  );

  const file = get(allValues, 'contact.profile')?.[0];
  const fileInfo = file ? `${file.name} (${Math.round(file.size / 1024)} KB)` : null;
  const showImage = !!(file && file.type && file.type.startsWith('image/'));
  const [thumbUrl, setThumbUrl] = useState(null);

  useEffect(() => {
    if (!showImage) { setThumbUrl(null); return; }
    const url = URL.createObjectURL(file);
    setThumbUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [showImage, file]);

  return (
    <div className="tba-review">
      <div className="tba-review-grid">
        <Section title={t('Submitter Information')}>
          <div className="tba-kv">
            <KV label={t('First name')} value={get(allValues, 'submitter.first_name')} />
            <KV label={t('Last name')} value={get(allValues, 'submitter.last_name')} />
            <KV label={t('Email address')} value={get(allValues, 'submitter.email')} />
            <KV label={t('PGP fingerprint')} value={get(allValues, 'submitter.pgpFingerprint')} />
            <KV label={t('Phone number including country code')} value={get(allValues, 'submitter.phoneNumber')} />
            <KV label={t('Role')} value={get(allValues, 'submitter.role')} />
          </div>
        </Section>

        <Section title={t('Section 1) Team Name')}>
          <div className="tba-kv">
            <KV label={t('Acronym used')} value={get(allValues, 'team.acronym')} />
            <KV label={t('Official team name')} value={get(allValues, 'team.teamName')} />
            <KV label={t('Host organisation')} value={get(allValues, 'team.hostOrg')} />
            <KV label={t('Main Country')} value={get(allValues, 'team.mainCountry')} />
            <KV label={t('Additional countries')} value={get(allValues, 'team.additionalCountries')} />
            <KV label={t('Team Establishment Date')} value={get(allValues, 'team.establishmentDate')} />
          </div>
        </Section>

        <Section title={t('Section 2) Type of Team')}>
          <div className="tba-kv">
            <div className="tba-kv__row">
              <div className="tba-kv__key">{t('Type')}</div>
              <div className="tba-kv__val"><Chips items={get(allValues, 'teamType.types')} /></div>
            </div>
            <KV label={t('Other type of team')} value={get(allValues, 'teamType.otherType')} />
          </div>
        </Section>

        <Section title={t('Section 3) Description of the constituency')}>
          <div className="tba-paragraph">{val(get(allValues, 'constituency'))}</div>
        </Section>

        <Section title={t('Section 4) Contact Information')}>
          <div className="tba-kv">
            <KV label={t('Postal Mailing Address of the team including country')} value={get(allValues, 'contact.postalAddress')} />
            <KV label={t('Team Email address')} value={get(allValues, 'contact.email')} />
            <KV label={t('Phone number including country code')} value={get(allValues, 'contact.primaryPhone')} />
            <KV label={t('Emergency phone number including country code')} value={get(allValues, 'contact.emergencyPhone')} />
            <KV label={t('PGP fingerprint')} value={get(allValues, 'contact.primaryPgp')} />
            <KV label={t('World Wide Web server')} value={get(allValues, 'contact.webServer')} />
            <KV label={t('Business hours')} value={get(allValues, 'contact.businessHours')} />
            <KV label={t('Time-zone')} value={get(allValues, 'contact.timeZone')} />
            <div className="tba-kv__row">
              <div className="tba-kv__key">{t('Team profile/logo')}</div>
              <div className="tba-kv__val">
                {thumbUrl ? (
                  <div className="tba-thumb">
                    <img src={thumbUrl} alt="Team profile/logo preview" className="tba-thumb__img" />
                    <div className="tba-thumb__meta">{fileInfo}</div>
                  </div>
                ) : (
                  <span>{fileInfo || '—'}</span>
                )}
              </div>
            </div>
          </div>
        </Section>

        <Section title={t('Section 5) Team Representatives')}>
          <div className="tba-two-col">
            <div>
              <h5 className="tba-subtitle">{t('Primary Team Representative')}</h5>
              <div className="tba-kv">
                <KV label={t('First and last name')} value={get(allValues, 'reps.primary.name')} />
                <KV label={t('Email address')} value={get(allValues, 'reps.primary.email')} />
                <KV label={t('PGP fingerprint')} value={get(allValues, 'reps.primary.pgp')} />
                <KV label={t('Phone number including country code')} value={get(allValues, 'reps.primary.phone')} />
              </div>
            </div>
            <div>
              <h5 className="tba-subtitle">{t('Secondary Team Representative')}</h5>
              <div className="tba-kv">
                <KV label={t('First and last name')} value={get(allValues, 'reps.secondary.name')} />
                <KV label={t('Email address')} value={get(allValues, 'reps.secondary.email')} />
                <KV label={t('PGP fingerprint')} value={get(allValues, 'reps.secondary.pgp')} />
                <KV label={t('Phone number including country code')} value={get(allValues, 'reps.secondary.phone')} />
              </div>
            </div>
          </div>
        </Section>

        <Section title={t('Section 6) Memberships')}>
          <div className="tba-kv">
            <KV label={t('Full member since')} value={get(allValues, 'memberships.firstMemberSince')} />
            <KV label={t('Listed since')} value={get(allValues, 'memberships.tfCsirtListedSince')} />
            <KV label={t('Accredited since')} value={get(allValues, 'memberships.tfCsirtAccreditedSince')} />
            <KV label={t('Name')} value={get(allValues, 'memberships.forumName')} />
            <KV label={t('URL public information')} value={get(allValues, 'memberships.forumUrl')} />
          </div>
        </Section>

        <Section title={t('Section 7) Cooperation and Collaboration')}>
          <div className="tba-paragraph">{val(get(allValues, 'cooperationAndCollaboration'))}</div>
        </Section>
      </div>

      <p className="tba-help" style={{ marginTop: 12 }}>{t('Review your entries, then click Submit.')}</p>
    </div>
  );
}

export default function TbaTemplateWizard() {
  const { t } = useTranslation();
  const methods = useForm({ defaultValues, mode: 'onChange' });
  const [stepIndex, setStepIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [stepIndex]);

  const next = async () => {
    const key = steps[stepIndex].key;
    const toValidate = stepRequiredFields[key] || [];
    const ok = toValidate.length ? await methods.trigger(toValidate) : true;
    if (ok) setStepIndex((s) => Math.min(s + 1, steps.length - 1));
  };
  const back = () => setStepIndex((s) => Math.max(s - 1, 0));

  const onSubmit = methods.handleSubmit(async (values) => {

    const fd = new FormData();
    fd.append('submitter', JSON.stringify(values.submitter));
    fd.append('team', JSON.stringify(values.team));
    fd.append('teamType', JSON.stringify(values.teamType));
    fd.append('constituency', values.constituency || '');
    const { profile, ...contactRest } = values.contact || {};
    fd.append('contact', JSON.stringify(contactRest));
    const fileList = values?.contact?.profile;
    if (fileList && fileList[0]) fd.append('profile', fileList[0]);
    fd.append('reps', JSON.stringify(values.reps));
    fd.append('memberships', JSON.stringify(values.memberships));
    fd.append('cooperationAndCollaboration', values.cooperationAndCollaboration || '');
    setIsLoading(true);
    const r = await fetch(`${BASE_URL}/api/tba/apply-for-tba`, { method: 'POST', body: fd });
    if (!r.ok) {
      setIsLoading(false);
      let er = 'Submit failed';
      try { const j = await r.json(); er = j.error || er; } catch (_) {}
      alert(er);
      return;
    }
    setIsLoading(false);
    //reset form
    alert('Submitted successfully');
    methods.reset(defaultValues);
    setStepIndex(0);
  });

  const step = steps[stepIndex].key;

  return (
    <FormProvider {...methods}>
      <div className="tba-wizard">
        <h2 className="tba-title">{t('TBA Template')}: list_v32.txt</h2>
      
      <p>
        {t("public key files for ")}
        <strong>*all*</strong> {t("keys referenced")}. .
      </p>
      <p>
        {t("You will find our contact data and public PGP/GPG key on")}:
        <a
          href="/support"
          target="_blank"
          rel="noopener noreferrer"
        >
          {" "} support
        </a>
      </p>
      <p>{t("Please note")} .</p>
      {/* <p>
        {t("All team-related information will be available from")}:
        <a
          href="/registry/index.html"
          target="_blank"
          rel="noopener noreferrer"
        >
          {" "} registry
        </a>
      </p> */}
        <StepHeader current={stepIndex} />

        {step === 'submitter' && <SubmitterStep />}
        {step === 'team' && <TeamStep />}
        {step === 'teamType' && <TeamTypeStep />}
        {step === 'constituency' && <ConstituencyStep />}
        {step === 'contact' && <ContactStep />}
        {step === 'reps' && <RepsStep />}
        {step === 'memberships' && <MembershipsStep />}
        {step === 'cooperationAndCollaboration' && <CoopStep />}
        {step === 'review' && <ReviewStep allValues={methods.getValues()} />}

        <div className="tba-actions">
          <button type="button" onClick={back} disabled={stepIndex === 0} className="tba-btn tba-btn--danger">
            {t('Back')}
          </button>
          {stepIndex < steps.length - 1 ? (
            <button type="button" onClick={next} className="tba-btn tba-btn--primary">
              {t('Next')}
            </button>
          ) : (
            <button type="button" disabled={isLoading} onClick={onSubmit} className="tba-btn tba-btn--success">
              {isLoading ? t('Submitting...') : t('Submit')}
            </button>
          )}
        </div>
      </div>
    </FormProvider>
  );
}
